// frontend/src/components/homepage/HowItWorksLive.js
//
// "Where Opportunities Find You" - the pinned three-stage story of what the
// product does: a live feed (stage 1), the AI scanning it (stage 2), a push on
// the phone (stage 3). On a wide screen the section is a 340vh scroll with a
// sticky 100vh pin; scroll progress through it picks the stage, fills the bar
// and the rail, tints the pin, and is published on `window.__how` for the
// smoke plume (SmokeCanvas) that only draws while the pin is holding.
//
// WHAT A SCROLL FRAME WRITES (final review #3): the stage class ONLY when the
// stage changes (it is React state, set through a ref-compared setter, so a
// frame inside one stage re-renders nothing); the rail and the bar's fill as
// `transform: scaleY / scaleX` - compositor-only, no layout - never as height
// or width; and the percent text (one text node, cheap). The pin box carries
// `contain: layout style` so a write inside it cannot invalidate layout
// outside it. The smoke is portalled out of the section, so containment
// never sees it.
//
// THREE BRANCHES, one component each, chosen once at the top:
//   * the prerenderer, or motion off  -> HowMobileStack: a static stack, no
//     on a screen under 1000px            task, no listener (also what crawlers
//                                       read - every word is real text there).
//   * motion off on a wide screen     -> the same desktop layout UNPINNED, at
//                                       stage 3, nothing registered anywhere.
//   * otherwise, AT EVERY WIDTH        -> the pinned, scroll-driven section.
//
// A PHONE GETS THE PINNED STORY TOO (owner round 2, 2026-09-04): the same
// three stages, stage bar, tints, explosions and smoke, in a two-column grid
// (steps beside the feed, never under it) at a compact scale - the
// `@media (max-width: 999px)` block of homepage.css - on a 260vh wrapper so a
// thumb scroll advances a stage. The smoke's phone budget is the engine's
// LIGHT plume (smokePlume.js), chosen by canvas width. The static stack is now
// only the motion-off and prerender picture.
//
// THE SCROLL DRIVER IS A DIRECT rAF THROTTLE, not a coordinator task (the
// ScannedTray precedent): it is one-shot per scroll event rather than a
// per-frame loop, and there is nothing to run between scrolls. The per-frame
// work - the feed - is HowStream's task on the shared chain. In development a
// third thing runs: a tiny coordinator task that watches `window.__forceHow`
// so the stages can be steered without scrolling (headless captures, the
// console). It is compiled out of production by the NODE_ENV check.
//
// `window.__how = { p, stage, active }` is the prototype's contract and is
// kept verbatim: the smoke engine is framework-free and reads it per frame.
// It is written on every evaluation and reset to inactive on unmount.

import React, { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import useMediaQuery from "@mui/material/useMediaQuery";
import SectionShell from "./SectionShell";
import HowSteps, { HOW_COPY } from "./HowSteps";
import HowStream from "./HowStream";
import HowMobileStack from "./HowMobileStack";
import useMotion from "../../motion/useMotion";
import useHydrated from "../../motion/useHydrated";
import useInViewGate, { isPrerendering } from "../../motion/useInViewGate";
import { addTask } from "../../motion/rafCoordinator";
import { getForcedHowProgress } from "../../homepage/howDebug";
import {
  HOW_WIDE_MIN_PX,
  RAIL_LEAD,
  pinProgress,
  stageForProgress,
} from "../../homepage/howFeed";

// The smoke is the most expensive thing on the page after the mesh, and it is
// only ever visible inside this section: split out, fetched when the section
// first comes near, gone when it leaves.
const SmokeCanvas = React.lazy(() => import("./SmokeCanvas"));

/**
 * The id of the smoke's portal target, rendered by pages/Homepage.js in the
 * page's fixed background stack (after the mesh, before the grain). The canvas
 * must sit in the SAME stacking context as the sections: AuthLayout's <main>
 * is `position: relative; z-index: 1`, so a canvas portalled to <body> is a
 * z 1 sibling of <main> that comes later in the DOM and paints over the whole
 * page - every section's z 2 is trapped inside <main> and cannot answer it.
 * Inside the slot, z 1 is above the mesh (z 0) and below every section (z 2),
 * which is what the prototype's flat <body> gave for free. No slot (a shell
 * that does not render one) means NO smoke - never a fallback to <body>.
 */
export const SMOKE_SLOT_ID = "hl-smoke-slot";

/** How far outside the viewport the section counts as "near" (arms the feed + smoke). */
const NEAR_ROOT_MARGIN = "40% 0px 40% 0px";

const isDev = process.env.NODE_ENV !== "production";

/** A scale factor as a short, stable string: 4 decimals is sub-pixel on any rail. */
const scale = (v) => Math.round(v * 1e4) / 1e4;

/** Publish the prototype's contract. */
const publishHow = (p, stage, active) => {
  if (typeof window === "undefined") return;
  window.__how = { p, stage, active };
};

/**
 * The shared desktop layout: eyebrow, English H2, sub, stage bar, steps, and
 * the stream. Pinned and static render the same markup; only the classes and
 * whether anything drives it differ.
 */
function HowLayout({ t, stage, isStatic, live, refs, wrapRef, pinClass, leads }) {
  return (
    <div
      className={isStatic ? "hl-how-pinwrap hl-how-static" : "hl-how-pinwrap"}
      ref={wrapRef}
      data-testid="hl-how-pinwrap"
    >
      <div className={pinClass} ref={refs.pin}>
        {!isStatic && (
          <div
            className={stage === 3 ? "hl-how-more hl-done" : "hl-how-more"}
            aria-hidden="true"
          >
            <span>{t.scrollMore}</span>
            <i />
          </div>
        )}
        <div className="hl-how-grid">
          <div>
            <div className="hl-eyebrow">{t.eyebrow}</div>
            <h2 className="hl-h2 hl-how-en-h2" dir="ltr" lang="en">
              {t.h2}
            </h2>
            <p className="hl-sub">{t.sub}</p>
            <div className="hl-how-bar" ref={refs.bar} data-testid="hl-how-bar">
              <b>
                {`${t.stageWord} `}
                <em>{stage}</em> / 3
              </b>
              <span className="hl-how-track">
                <i ref={refs.fill} style={isStatic ? { transform: "scaleX(1)" } : undefined} />
              </span>
              <span className="hl-how-pct" ref={refs.pct}>
                {isStatic ? "100%" : "0%"}
              </span>
            </div>
            <HowSteps t={t} stage={stage} railRef={refs.rail} railFull={isStatic} />
          </div>
          <HowStream t={t} stage={stage} live={live} isStatic={isStatic} leads={leads} />
        </div>
      </div>
    </div>
  );
}

/** Motion off on a wide screen: the finished picture, unpinned, nothing running. */
function HowStatic({ t, leads }) {
  const refs = { pin: null, bar: null, fill: null, pct: null, rail: null };
  return (
    <HowLayout
      t={t}
      leads={leads}
      stage={3}
      isStatic
      live={false}
      refs={refs}
      wrapRef={null}
      pinClass="hl-how-pin hl-how-t3"
    />
  );
}

/** The pinned, scroll-driven section. */
function HowPinned({ t, leads }) {
  const [stage, setStage] = useState(1);
  const stageRef = useRef(1);
  const wrapRef = useRef(null);
  const pinRef = useRef(null);
  const barRef = useRef(null);
  const fillRef = useRef(null);
  const pctRef = useRef(null);
  const railRef = useRef(null);
  const mountedRef = useRef(false);

  // "Near" arms the feed task and mounts the smoke; a generous margin so both
  // are ready before the pin starts holding, not a beat after.
  const [gateRef, near] = useInViewGate({ rootMargin: NEAR_ROOT_MARGIN });
  const setWrap = useCallback(
    (node) => {
      wrapRef.current = node;
      gateRef(node);
    },
    [gateRef]
  );

  const setStageNow = useCallback((next) => {
    stageRef.current = next;
    setStage(next);
  }, []);

  // The scroll driver.
  useEffect(() => {
    let frame = 0;

    const evaluate = () => {
      frame = 0;
      const wrap = wrapRef.current;
      if (!wrap) return;
      const forced = getForcedHowProgress();
      let p;
      let active;
      if (forced !== null) {
        p = forced;
        active = true;
      } else {
        const rect = wrap.getBoundingClientRect();
        ({ p, active } = pinProgress(rect.top, wrap.offsetHeight, window.innerHeight));
      }
      const next = stageForProgress(p);
      // The stage class is written by React, and only here, only on a change.
      if (next !== stageRef.current) setStageNow(next);
      // Per-scroll numbers go straight to the elements: a re-render per scroll
      // frame would buy nothing a style write does not already do. Transforms,
      // not height / width: those are layout properties, and a phone paid a
      // layout + a wide style recalc for every scroll frame of the pin.
      if (railRef.current) railRef.current.style.transform = `scaleY(${scale(Math.min(1, p * RAIL_LEAD))})`;
      if (fillRef.current) fillRef.current.style.transform = `scaleX(${scale(p)})`;
      if (pctRef.current) pctRef.current.textContent = `${Math.round(p * 100)}%`;
      publishHow(p, next, active);
    };

    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(evaluate);
    };

    evaluate();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    // Development only: follow `window.__forceHow` without a scroll event.
    let removeWatch = null;
    if (isDev) {
      let lastForced = getForcedHowProgress();
      removeWatch = addTask(() => {
        const forced = getForcedHowProgress();
        if (forced !== lastForced) {
          lastForced = forced;
          evaluate();
        }
      });
    }

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) cancelAnimationFrame(frame);
      if (removeWatch) removeWatch();
      publishHow(0, 1, false);
    };
  }, [setStageNow]);

  // The stage bar's tick: restarting a CSS animation needs the class removed,
  // a reflow forced, and the class re-added. Not on mount - the prototype only
  // ticks on a CHANGE.
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    const bar = barRef.current;
    if (!bar) return;
    bar.classList.remove("hl-how-tick");
    void bar.offsetWidth;
    bar.classList.add("hl-how-tick");
  }, [stage]);

  // The portal target is a DOM node the page shell renders, so it exists only
  // after the first commit: resolved in an effect, never during render.
  const [smokeSlot, setSmokeSlot] = useState(null);
  useEffect(() => {
    setSmokeSlot(document.getElementById(SMOKE_SLOT_ID));
  }, []);

  const forcedForDebug = isDev && getForcedHowProgress() !== null;
  const live = near || forcedForDebug;

  return (
    <>
      <HowLayout
        t={t}
        leads={leads}
        stage={stage}
        isStatic={false}
        live={live}
        refs={{ pin: pinRef, bar: barRef, fill: fillRef, pct: pctRef, rail: railRef }}
        wrapRef={setWrap}
        pinClass={`hl-how-pin hl-how-t${stage}`}
      />
      {/* The smoke sits in the page's fixed background stack (z 1: above the
          mesh, below every section), so it is portalled OUT of this section's
          stacking context - into the page shell's slot, never to <body>; see
          SMOKE_SLOT_ID. A fixed canvas inside a z-indexed section would paint
          above the section's own content and blend with nothing. */}
      {live &&
        smokeSlot &&
        createPortal(
          <Suspense fallback={null}>
            <SmokeCanvas />
          </Suspense>,
          smokeSlot
        )}
    </>
  );
}

/**
 * @param {object} props
 * @param {boolean} [props.isHebrew]
 */
/**
 * @param {object} props
 * @param {boolean} [props.isHebrew]
 * @param {object[]} [props.leads] the demo deck every still and the live feed
 *   draw their posts from. Defaults to the homepage's own (HowStream and
 *   HowMobileStack each fall back to `heroData`'s LEADS), so `Homepage.js`
 *   passes nothing and renders exactly what it did. A profession page passes
 *   its deck, or the section tells its story with someone else's trade.
 *   The NOISE posts are deliberately NOT a prop: they are profession-free
 *   chatter (a sofa, a kindergarten, a hike) and read correctly under any
 *   hero. The push mock's RESTING line follows the deck too: the homepage
 *   keeps its labour-lawyer `pushDefault`, a profession page rests on its own
 *   card (HowStream's `restingPush`).
 */
export default function HowItWorksLive({ isHebrew = true, leads }) {
  const t = isHebrew ? HOW_COPY.he : HOW_COPY.en;
  const wide = useMediaQuery(`(min-width:${HOW_WIDE_MIN_PX}px)`, { noSsr: true });
  const { off } = useMotion();
  // The prerendered snapshot carries the stack; the hydrating client must
  // render the same on its first pass and swap to the pinned story one render
  // later (synchronously, before paint). See motion/useHydrated.js.
  const hydrated = useHydrated();

  // The still pictures: the prerenderer always gets the stack (its 800px
  // viewport would land on the phone layout anyway, and the stack is what the
  // section's SEO rule was written against); motion off gets the stack on a
  // narrow screen and the unpinned desktop layout on a wide one.
  const stack = isPrerendering() || !hydrated || (off && !wide);

  return (
    <SectionShell
      id="how-it-works"
      mesh="pipeline"
      meshAlpha=".55"
      wrap={false}
      // The pin owns its own vertical rhythm; the stack keeps the shared one.
      sx={stack ? undefined : { py: 0 }}
    >
      {stack ? (
        <HowMobileStack t={t} leads={leads} />
      ) : off ? (
        <HowStatic t={t} leads={leads} />
      ) : (
        <HowPinned t={t} leads={leads} />
      )}
    </SectionShell>
  );
}
